class CreadorasHub extends HTMLElement {
  // Init in connectedCallback (Dawn convention): children are guaranteed parsed
  // here, so it's robust regardless of how/when the script loads.
  connectedCallback() {
    if (this._init) return; // connectedCallback can fire again if the node moves
    this._init = true;
    this.api = this.dataset.api;
    this.creadora = new URLSearchParams(location.search).get('creadora')?.trim().toLowerCase() || '';
    this.statusEl = this.querySelector('[data-status]');
    this.loaderEl = this.querySelector('[data-loader]');
    this.listEl = this.querySelector('[data-list]');
    this.greetingEl = this.querySelector('[data-greeting]');
    this.data = null;
    this.querySelector('[data-business-toggle]').addEventListener('click', () => this.toggleMode());
    this.load();
  }

  toggleMode() {
    const next = this.dataset.mode === 'negocio' ? 'creadora' : 'negocio';
    this.dataset.mode = next;
    this.querySelector('[data-business-toggle]').textContent =
      next === 'negocio' ? 'Volver a la vista de creadora' : '¿Eres un negocio aliado?';
  }

  showLoader() { this.loaderEl.hidden = false; this.statusEl.hidden = true; this.listEl.hidden = true; }
  showStatus(msg) { this.loaderEl.hidden = true; this.listEl.hidden = true; this.statusEl.hidden = false; this.statusEl.textContent = msg; }

  async load() {
    if (!this.api) return this.showStatus('Configuración pendiente.');
    if (!this.creadora) return this.showStatus('Escanea tu tarjeta para ver tus beneficios.');
    this.showLoader();
    let data;
    try {
      // cache-bust so a fresh load never shows a stale catalog response
      const url = `${this.api}?creadora=${encodeURIComponent(this.creadora)}&_=${Date.now()}`;
      const res = await fetch(url);
      data = await res.json();
    } catch (e) {
      return this.showStatus('No pudimos cargar los beneficios. Revisa tu conexión.');
    }
    this.data = data;
    if (!data.ok || !data.known) {
      return this.showStatus('No reconocemos esta tarjeta. Escríbenos si crees que es un error.');
    }
    this.render();
  }

  render() {
    const { creadora, negocios } = this.data;
    this.greetingEl.textContent = `Hola, ${creadora.name} 👋` +
      (creadora.instagram ? `  ·  ${creadora.instagram}` : '');
    this.greetingEl.hidden = false;
    this.loaderEl.hidden = true;
    this.statusEl.hidden = true;
    this.listEl.hidden = false;
    this.listEl.innerHTML = '';
    negocios.forEach(n => this.listEl.appendChild(this.card(n)));
  }

  card(negocio) {
    const li = document.createElement('li');
    li.className = 'creadoras-hub__card';
    li.innerHTML = `
      <h3>${escapeHtml(negocio.name)}</h3>
      <p class="creadoras-hub__district">${escapeHtml(negocio.district || '')}</p>
      <div class="creadoras-hub__offers"></div>
      <form class="creadoras-hub__validate">
        <label>Código del negocio
          <input type="text" name="code" class="creadoras-hub__code" autocomplete="off" required>
        </label>
        <button type="submit" class="button">Registrar visita</button>
        <p class="creadoras-hub__msg" data-msg hidden></p>
      </form>`;

    const offersEl = li.querySelector('.creadoras-hub__offers');
    negocio.offers.forEach(o => {
      const row = document.createElement('label');
      row.className = 'creadoras-hub__offer';
      row.dataset.offerId = o.offerId;
      row.dataset.empty = o.remaining <= 0;
      // The checkbox is hidden by CSS unless the hub is in "negocio" mode, so a
      // creadora browsing her benefits sees each offer but nothing tickable.
      row.innerHTML = `
        <input type="checkbox" class="creadoras-hub__check" value="${escapeHtml(o.offerId)}" ${o.remaining <= 0 ? 'disabled' : ''}>
        <span class="creadoras-hub__offer-name">${escapeHtml(o.offer)}</span>
        <span class="creadoras-hub__remaining" data-remaining>${remainingLabel(o)}</span>`;
      row._offer = o; // keep offer meta for in-place updates after a redemption
      offersEl.appendChild(row);
    });

    li.querySelector('.creadoras-hub__validate').addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitRedemption(li, negocio);
    });
    return li;
  }

  async submitRedemption(li, negocio) {
    const msg = li.querySelector('[data-msg]');
    const btn = li.querySelector('.creadoras-hub__validate button[type="submit"]');
    const code = li.querySelector('.creadoras-hub__code').value.trim();
    const offerIds = [...li.querySelectorAll('.creadoras-hub__check:checked')].map(i => i.value);
    if (!offerIds.length) return showMsg(msg, 'Selecciona al menos un beneficio.', 'error');

    btn.disabled = true; // guard against a double-tap firing two POSTs
    showMsg(msg, 'Registrando…', 'info');
    let out;
    try {
      const res = await fetch(this.api, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' }, // text/plain avoids CORS preflight
        body: JSON.stringify({ creadora: this.creadora, code, offerIds })
      });
      out = await res.json();
    } catch (e) {
      btn.disabled = false;
      return showMsg(msg, 'Error de red. Intenta de nuevo.', 'error');
    }
    btn.disabled = false;

    if (!out.ok) {
      return showMsg(msg, out.error === 'codigo_invalido' ? 'Código incorrecto.' : 'No se pudo registrar.', 'error');
    }

    // Update counters in place from out.results — do NOT re-render, which would
    // destroy this message and the card DOM. Surface every rejection by name so
    // staff never hand over an item that was actually refused.
    const nameOf = id => (negocio.offers.find(o => o.offerId === id) || {}).offer || id;
    const accepted = [], rejected = [];
    out.results.forEach(r => {
      const row = li.querySelector(`.creadoras-hub__offer[data-offer-id="${r.offerId}"]`);
      const cb = row && row.querySelector('.creadoras-hub__check');
      if (cb) cb.checked = false; // clear both accepted and rejected selections
      if (r.accepted) {
        accepted.push(nameOf(r.offerId));
        if (row) {
          row._offer.remaining = r.remaining;
          row.dataset.empty = r.remaining <= 0;
          row.querySelector('[data-remaining]').textContent = remainingLabel(row._offer);
          if (cb) cb.disabled = r.remaining <= 0;
        }
      } else {
        const why = r.reason === 'sin_cupo' ? 'sin cupo'
                  : r.reason === 'no_pertenece' ? 'no pertenece a este código'
                  : 'rechazado';
        rejected.push(`${nameOf(r.offerId)} (${why})`);
      }
    });

    const parts = [];
    if (accepted.length) parts.push(`✓ Entregar en ${negocio.name}: ${accepted.join(', ')}.`);
    if (rejected.length) parts.push(`✗ NO entregar: ${rejected.join('; ')}.`);
    showMsg(msg, parts.join('  '), rejected.length ? 'error' : 'info');
  }
}

function remainingLabel(o) {
  if (o.period === 'unico') return o.remaining > 0 ? 'Disponible' : 'Ya usado';
  return o.remaining > 0 ? `Te queda ${o.remaining} este mes` : 'Sin cupo este mes';
}
function showMsg(el, text, kind) { el.hidden = false; el.textContent = text; el.dataset.kind = kind; }
function escapeHtml(s) { return String(s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c])); }

customElements.define('creadoras-hub', CreadorasHub);
